import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import BrandLoader from '../shared/brand/BrandLoader';
import Logo from '../shared/brand/Logo';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../store';
import { fetchStructureTemplates, fetchBrandingTemplates, deleteStructureTemplate, deleteBrandingTemplate, deleteStructureTemplatesBulk } from '../../store/actions/TemplateActions';
import { fetchTemplates, deleteTableTemplate } from '../../store/actions/BatchTables/BatchTablesActions';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import { useHistory, useLocation } from 'react-router-dom';
import { PiPaletteLight, PiListLight, PiPlusLight, PiTrashLight, PiClockLight, PiTableLight, PiHourglassLight, PiBookOpenTextLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import { fetchReviews, deleteReview, bulkDeletePlaybooks } from '../../store/actions/Review/ReviewActions';
import { ReviewListItem, ReviewStatus } from '../../types/review';
import CreatePlaybookForm from '../Review/CreatePlaybookForm';
import Table from '../shared/TailwindComponents/Table';
import Header from '../shared/TailwindComponents/Header';
import TemplateForm from './TemplateForm';
import { formatDateTime } from '../../utils/functions';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import PersonCell from '../shared/access/PersonCell';
import { TEMPLATE_LIBRARIES, templateLibrary } from './templateLibraries';
import { useOptionalBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { ownerOf } from '../shared/access/accessModel';
import clsx from 'clsx';
import StructureForm from './StructureForm';
import TableColumnsForm from './TableColumnsForm';
import { Tooltip } from '@mui/material';
import { useProcessingPubSub } from '../../hooks/useProcessingPubSub';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import DeleteTemplateConfirmation from './DeleteTemplateConfirmation';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, templatesListTourSteps } from '../shared/FeatureTour';
 
import ActivityDrawer from '../shared/activity/ActivityDrawer';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';

const processingStatusToColorMap: Record<string, BadgeColor> = {
  processing: 'blue',
  pending: 'orange',
  completed: 'green',
  failed: 'red',
  ready: 'green',
};
 
interface Template {
  id: string;
  TemplateName: string;
  DraftType: string;
  OwnerType: string;
  Version: number;
  CreatedAt: string;
  UpdatedAt: string;
  status?: string; // processing status: pending, processing, completed, failed
  Owner?: {
    UserID: string;
    Name: string;
    Email: string;
    ProfilePicture: string | null;
    Initials: string;
  };
  Structure?: { section: string; subsections: string[] }[] | {  
    suggested_name?: string;
    description?: string;
    outline?: { level: number; title: string; content_prompt: string }[];
    tone?: string;
    writing_style?: string;
    formatting_hints?: { [key: string]: any };
    additional?: { [key: string]: any };
  };
  Branding?: {
    Typography: { FontType: string };
    HeaderFooter: { HeaderText: string };
  };
  SharedWith?: { UserID: string; Name: string; Email: string; ProfilePicture: string | null; Initials: string }[] | string;
}
 
interface TemplatesListProps {}
 
const TemplatesList: React.FC<TemplatesListProps> = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  // The archived view's count rides in the filter bar's own badge (R4-01).
  const [trashCount, setTrashCount] = useState<{ showing: number; total: number }>({ showing: 0, total: 0 });
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const location = useLocation();
  const history = useHistory();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [selectedTemplates, setSelectedTemplates] = useState<string[]>([]);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get('type');
  const activeTab = (
    typeParam === 'table-columns' ? 'table-columns' :
    typeParam === 'playbooks' ? 'playbooks' :
    typeParam === 'branding' ? 'branding' :
    'structure'
  ) as 'structure' | 'branding' | 'table-columns' | 'playbooks';
  const isAddForm = location.pathname === '/templates/add';
  const templateId = location.pathname.startsWith('/templates/') && !isAddForm ? location.pathname.split('/templates/')[1]?.split('?')[0] : undefined;
  const isInvalidType = typeParam && !['structure', 'branding', 'table-columns', 'playbooks'].includes(typeParam);

  // ─── PubSub: live processing tracking ──────────────────────────────
  const fetchRef = useRef<() => void>(() => {});
  const processingStatuses = new Set(['processing', 'pending']);

  const { processingStates, reattachListener } = useProcessingPubSub({
    entityType: 'template',
    getItemUuid: (item: any) => item.id,
    isProcessing: (item: any) => processingStatuses.has(item.status || ''),
    onRefetch: () => fetchRef.current(),
    enabled: !isAddForm && !templateId && activeTab !== 'playbooks',
  });

  // ─── PubSub: playbook extraction tracking ──────────────────────────
  const playbookFetchRef = useRef<() => void>(() => {});
  const { processingStates: playbookProcessingStates, joinProcessingGroups: joinPlaybookGroups, reattachListener: reattachPlaybookListener } = useProcessingPubSub({
    entityType: 'playbook',
    getGroupName: (item: any) => item.extraction_status === 'processing' ? `playbook_${item.uuid}` : null,
    getItemUuid: (item: any) => item.uuid,
    isProcessing: (item: any) => item.extraction_status === 'processing',
    onRefetch: () => playbookFetchRef.current(),
    enabled: !isAddForm && !templateId && activeTab === 'playbooks',
  });
 
  useEffect(() => {
    if (isInvalidType) {
      history.replace('/templates?type=structure');
    }
  }, [isInvalidType, history]);

  /* The top-bar breadcrumb follows the selected library: Templates › <Type>.
     The type mechanism stays `?type=` (left as-is); only the crumb is new, and
     it updates as the type changes and clears on unmount. */
  const breadcrumbsCtx = useOptionalBreadcrumbs();
  useEffect(() => {
    const setBreadcrumbs = breadcrumbsCtx?.setBreadcrumbs;
    if (!setBreadcrumbs) return;
    const lib = templateLibrary(activeTab);
    setBreadcrumbs([
      { name: t('Templates'), href: '/templates', current: false },
      {
        name: lib ? t(lib.label) : t('Templates'),
        href: `/templates?type=${activeTab}`,
        current: true,
      },
    ]);
    return () => setBreadcrumbs([]);
  }, [activeTab, breadcrumbsCtx, t]);

  // ─── Tour ──────────────────────────────────────────────────────────
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'templates-list',
    steps: templatesListTourSteps,
    readyToShow: !isLoading && !isAddForm && !templateId && !showTrash && templates.length > 0,
    autoStartDelay: 600,
  });
 
  /* One list of libraries - `templateLibraries` - read here for the tabs and
     by the creation form for its type switcher, so a library cannot be in the
     tab strip and missing from the switcher. */
  const TAB_ICONS: Record<string, React.ReactElement> = {
    PiListLight: <PiListLight size={16} />,
    PiTableLight: <PiTableLight size={16} />,
    PiBookOpenTextLight: <PiBookOpenTextLight size={16} />,
    PiPaletteLight: <PiPaletteLight size={16} />,
  };
  const templateTabs = TEMPLATE_LIBRARIES.map((lib) => ({
    label: t(lib.label),
    value: lib.id,
    icon: TAB_ICONS[lib.icon],
  }));
 
  const templateFilters = [
    {
      id: 'OwnerType',
      name: t('Owner type'),
      options: [
        { value: 'personal', label: t('Personal') },
        { value: 'firm-wide', label: t('Firm-Wide') },
      ],
    },
    {
      id: 'DraftType',
      name: t('Draft type'),
      options: [
        { value: 'nda', label: t('NDA') },
        { value: 'contract', label: t('Contract') },
        { value: 'memo', label: t('Memo') },
        { value: 'pleading', label: t('Pleading') },
      ],
    },
  ];
 
  const actions = [
    {
      label: activeTab === 'playbooks' ? t('Add playbook') : t('Add template'),
      icon: <PiPlusLight className='w-4 h-4' />,
      onClick: () => history.push(`/templates/add?type=${activeTab}`),
    },
  ];
 
  const handleTabClick = (tabValue: string) => {
    history.push(`/templates?type=${tabValue}`);
    setTemplates([]);
    setPage(1);
    setSelectedFilters({});
    setSelectedTemplates([]);
  };
 
  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
  };
 
  const handleSortChange = (newSortBy: string, newSortOrder?: 'asc' | 'desc') => {
    // Handle both direct calls from table headers (with two params)
    // and calls from FilterBarWithTabs (with encoded value like "field_order")
    if (newSortOrder) {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
    } else {
      const parts = newSortBy.split('_');
      const order = parts.pop() as 'asc' | 'desc';
      const field = parts.join('_');
      setSortBy(field);
      setSortOrder(order);
    }
  };
 
  const handleSearchChange = (query: string) => setSearchQuery(query);
  const handleClearAllFilters = () => setSelectedFilters({});
 
  const PAGE_SIZE = 50;
 
  // ─── Table Columns Templates (separate data model) ────────────────
  const [tableColumnTemplates, setTableColumnTemplates] = useState<any[]>([]);
  const [isLoadingTableCols, setIsLoadingTableCols] = useState(false);

  const fetchTableColumnTemplates = useCallback(async () => {
    setIsLoadingTableCols(true);
    try {
      const result = await dispatch(fetchTemplates() as any);
      const items = result?.items || [];
      setTableColumnTemplates(items);
      setTotalTemplates(items.length);
    } catch {
      setTableColumnTemplates([]);
    } finally {
      setIsLoadingTableCols(false);
    }
  }, [dispatch]);

  // ─── Playbooks (Review playbooks) ─────────────────────────────────
  const [playbooks, setPlaybooks] = useState<ReviewListItem[]>([]);
  const [isLoadingPlaybooks, setIsLoadingPlaybooks] = useState(false);
  const [playbooksTotalItems, setPlaybooksTotalItems] = useState(0);


  const fetchPlaybookTemplates = useCallback(async () => {
    setIsLoadingPlaybooks(true);
    try {
      const result = await dispatch(fetchReviews({
        page,
        page_size: PAGE_SIZE,
        search: searchQuery || undefined,
      }) as any);
      if (result) {
        const items = result.items || [];
        setPlaybooks(items);
        setPlaybooksTotalItems(result.total || 0);
        setTotalTemplates(result.total || 0);
        // Join PubSub groups for any playbooks with active extraction
        joinPlaybookGroups(items);
      }
    } catch {
      setPlaybooks([]);
    } finally {
      setIsLoadingPlaybooks(false);
    }
  }, [dispatch, page, searchQuery, joinPlaybookGroups]);

  const listRequestSeq = useRef(0);

  /*
    B8: only the NEWEST list request may write the list. Switching tab or
    filter while a request is in flight used to let the older response land
    last and overwrite the new tab's list (structure templates under the
    Branding tab). Every fresh load takes a number; a response that is no
    longer the latest is dropped, and a load-more from an older list is too.
  */
  const fetchInitialTemplates = useCallback(async () => {
    const seq = ++listRequestSeq.current;
    if (activeTab === 'table-columns' || activeTab === 'playbooks') {
      // These tabs keep their own loading flags; clear the shared one so a
      // dropped structure/branding response cannot leave it spinning.
      setIsLoading(false);
      if (activeTab === 'table-columns') fetchTableColumnTemplates();
      else fetchPlaybookTemplates();
      return;
    }
    setIsLoading(true);
    setPage(1);
    const fetchAction = activeTab === 'structure' ? fetchStructureTemplates : fetchBrandingTemplates;
    const response = await dispatch(fetchAction({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: 1,
      page_size: PAGE_SIZE,
    }));
    if (seq !== listRequestSeq.current) return;
    const { templates: newTemplates = [], total_templates = 0, totalPages = 0 } = response || {};
    setTemplates(newTemplates);
    setPage(1);
    const isLastPage = newTemplates.length < PAGE_SIZE || 1 >= totalPages;
    setHasMore(!isLastPage);
    setTotalTemplates(total_templates);
    setIsLoading(false);
  }, [dispatch, activeTab, searchQuery, selectedFilters, sortBy, sortOrder, fetchTableColumnTemplates]);
 
  const loadMoreTemplates = useCallback(async () => {
    if (activeTab === 'table-columns' || activeTab === 'playbooks') return;
    const seq = listRequestSeq.current;
    setIsLoading(true);
    const nextPage = page + 1;
    const fetchAction = activeTab === 'structure' ? fetchStructureTemplates : fetchBrandingTemplates;
    const response = await dispatch(fetchAction({
      searchTerm: searchQuery,
      filters: selectedFilters,
      sortBy,
      sortOrder,
      page: nextPage,
      page_size: PAGE_SIZE,
    }));
    if (seq !== listRequestSeq.current) return;
    const { templates: newTemplates = [], total_templates = 0, totalPages = 0 } = response || {};
    setTemplates(prev => [...prev, ...newTemplates]);
    setPage(nextPage);
    const isLastPage = newTemplates.length < PAGE_SIZE || nextPage >= totalPages;
    setHasMore(!isLastPage);
    setTotalTemplates(total_templates);
    setIsLoading(false);
  }, [dispatch, activeTab, page, searchQuery, selectedFilters, sortBy, sortOrder]);
 
  // Keep fetchRef in sync
  useEffect(() => {
    fetchRef.current = fetchInitialTemplates;
    playbookFetchRef.current = fetchInitialTemplates;
  }, [fetchInitialTemplates]);

  // Re-attach PubSub listener when navigating back from add/edit form
  const prevIsFormRef = useRef(isAddForm || !!templateId);
  useEffect(() => {
    const isForm = isAddForm || !!templateId;
    if (prevIsFormRef.current && !isForm) {
      reattachListener();
      reattachPlaybookListener();
    }
    prevIsFormRef.current = isForm;
  }, [isAddForm, templateId, reattachListener, reattachPlaybookListener]);

  useEffect(() => {
    if (!isAddForm && !templateId && !isInvalidType) {
      setTemplates([]);
      setPage(1);
      fetchInitialTemplates();
    }
  }, [activeTab, searchQuery, selectedFilters, sortBy, sortOrder, fetchInitialTemplates, isAddForm, templateId, isInvalidType]);

  // Poll for status updates when items are in processing state
  useEffect(() => {
    if (isAddForm || templateId) return;
    const hasProcessingItems = templates.some((t) => processingStatuses.has(t.status || ''));
    if (!hasProcessingItems) return;

    const interval = setInterval(() => {
      fetchInitialTemplates();
    }, 8000);

    return () => clearInterval(interval);
  }, [templates, isAddForm, templateId, fetchInitialTemplates]);
 
  /*
    WHO MADE IT, then WHO CAN SEE IT - in that order, on all three libraries.

    The access column was called three different things depending on which tab
    you were on: `Has Access` here and on playbooks, `Access` on structure. One
    name, and it is a sentence fragment rather than a heading, so it is
    sentence case: "Has access".

    Column names are also the persisted width keys (`table:<route>:<tableId>`),
    so renaming one retires whatever width the user had dragged it to. That is
    the right trade for one reset against a permanently inconsistent header.
  */
  const columns = useMemo(() => {
    if (activeTab === 'table-columns') return ['Name', 'Category', 'Columns', 'Created by', 'Has access', 'Updated At', 'Actions'];
    if (activeTab === 'playbooks') return ['Name', 'Agreement Types', 'Clause Rules', 'Global Rules', 'Created by', 'Has access', 'Updated At', 'Actions'];
    return ['Name', 'Document Type', 'Status', 'Sections', 'Created by', 'Has access', 'Updated At', 'Actions'];
  }, [activeTab]);
 
  const sortableColumns = ['Name', 'Updated At'];
 
  const handleDeleteTemplate = (templateId: string) => {
    const isPlaybook = activeTab === 'playbooks';
    const isTableCol = activeTab === 'table-columns';

    // Block deletion of global PONS templates
    if (isPlaybook) {
      const pb = playbooks.find(p => p.uuid === templateId);
      if (pb?.is_global) return;
    } else if (isTableCol) {
      const tpl = tableColumnTemplates.find((t: any) => (t.uuid || t.id) === templateId);
      if (tpl?.is_global) return;
    }

    const entityLabel = isPlaybook ? t('playbook') : isTableCol ? t('column template') : t('template');
    const description = isPlaybook
      ? t('This will permanently delete this playbook, including all clause rules and global rules. This action cannot be undone.')
      : isTableCol
        ? t('This will permanently delete this column template. This action cannot be undone.')
        : t('This will permanently delete this template from your library. This action cannot be undone.');

    dispatch(showModal({
      type: 'custom' as any,
      message: isPlaybook
        ? t('Are you sure you want to delete this playbook?')
        : t('Are you sure you want to delete this template?'),
      icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
      width: '500px',
      content: (
        <DeleteTemplateConfirmation
          count={1}
          entityLabel={entityLabel}
          description={description}
          showDeleteSessions={isPlaybook}
          onConfirm={async (deleteSessions?: boolean) => {
            if (activeTab === 'playbooks') {
              await dispatch(deleteReview(templateId, deleteSessions || false) as any);
            } else if (activeTab === 'table-columns') {
              await dispatch(deleteTableTemplate(templateId) as any);
            } else if (activeTab === 'structure') {
              await dispatch(deleteStructureTemplate(templateId));
            } else {
              await dispatch(deleteBrandingTemplate(templateId));
            }
            dispatch(hideModal());
            fetchInitialTemplates();
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
      showModal: true,
    }));
  };
 
  const tableActions = [
    {
      label: t('Activity'),
      onClick: (row: any) => {
        let name = '';
        if (activeTab === 'table-columns') {
          const tpl = tableColumnTemplates.find((t: any) => (t.uuid || t.id) === row.id);
          name = tpl?.name || '';
          setAuditTarget({ type: 'table_template', id: row.id, name });
        } else {
          const tpl = templates.find(t => t.id === row.id);
          name = tpl?.TemplateName || '';
          setAuditTarget({ type: 'template', id: row.id, name });
        }
      },
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete template'),
      onClick: (row: any) => handleDeleteTemplate(row.id),
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
      hidden: (row: any) => row._isGlobal,
    },
  ];

  const playbookActions = [
    {
      label: t('Activity'),
      onClick: (row: any) => {
        const pb = playbooks.find(p => p.uuid === row.id);
        setAuditTarget({ type: 'playbook', id: row.id, name: pb?.name || '' });
      },
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete playbook'),
      onClick: (row: any) => handleDeleteTemplate(row.id),
      icon: <PiTrashLight className="h-4 w-4" />,
      isDestructive: true,
      hidden: (row: any) => row._isGlobal,
    },
  ];

  const handleBulkDeleteTemplates = (ids: string[]) => {
    const isPlaybook = activeTab === 'playbooks';
    const isTableCol = activeTab === 'table-columns';

    // Filter out global items - they cannot be deleted
    let filteredIds = ids;
    if (isPlaybook) {
      const globalUuids = new Set(playbooks.filter(pb => pb.is_global).map(pb => pb.uuid));
      filteredIds = ids.filter(id => !globalUuids.has(id));
    } else if (isTableCol) {
      const globalUuids = new Set(tableColumnTemplates.filter((t: any) => t.is_global).map((t: any) => t.uuid || t.id));
      filteredIds = ids.filter(id => !globalUuids.has(id));
    }

    if (filteredIds.length === 0) {
      dispatch(showFeedbackModal({
        modalType: 'warning',
        message: isPlaybook
          ? t('Please select at least one playbook.')
          : t('Please select at least one template.'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    const entityLabel = isPlaybook ? t('playbook') : isTableCol ? t('column template') : t('template');
    const entityLabelPlural = isPlaybook ? t('playbooks') : isTableCol ? t('column templates') : t('templates');
    const noun = filteredIds.length === 1 ? entityLabel : entityLabelPlural;
    const description = isPlaybook
      ? filteredIds.length === 1
        ? t('This will permanently delete the selected playbook, including all clause rules and global rules. This action cannot be undone.')
        : t('This will permanently delete the selected playbooks, including all clause rules and global rules. This action cannot be undone.')
      : filteredIds.length === 1
        ? t(`This will permanently delete the selected ${entityLabel}. This action cannot be undone.`)
        : t(`This will permanently delete the selected ${entityLabelPlural}. This action cannot be undone.`);

    dispatch(showModal({
      type: 'custom' as any,
      message: filteredIds.length === 1
        ? (isPlaybook ? t('Are you sure you want to delete this playbook?') : t('Are you sure you want to delete this template?'))
        : (isPlaybook ? t('Are you sure you want to delete these playbooks?') : t('Are you sure you want to delete these templates?')),
      icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
      width: '500px',
      content: (
        <DeleteTemplateConfirmation
          count={filteredIds.length}
          entityLabel={entityLabel}
          entityLabelPlural={entityLabelPlural}
          description={description}
          showDeleteSessions={isPlaybook}
          onConfirm={async (deleteSessions?: boolean) => {
            if (activeTab === 'structure') {
              await dispatch(deleteStructureTemplatesBulk(filteredIds) as any);
            } else if (activeTab === 'playbooks') {
              const result = await dispatch(bulkDeletePlaybooks(filteredIds, deleteSessions || false) as any);
              if (result?.denied?.length > 0) {
                dispatch(showFeedbackModal({
                  modalType: 'warning',
                  message: `${result.denied.length} ${t('playbook(s) could not be deleted — admin access required.')}`,
                  showModal: true,
                  duration: 5000,
                }));
              }
            } else {
              for (const id of filteredIds) {
                if (activeTab === 'table-columns') {
                  await dispatch(deleteTableTemplate(id) as any);
                } else {
                  await dispatch(deleteBrandingTemplate(id) as any);
                }
              }
            }
            dispatch(hideModal());
            fetchInitialTemplates();
            setSelectedTemplates([]);
          }}
          onCancel={() => dispatch(hideModal())}
        />
      ),
      onCancel: () => {},
      showModal: true,
    }));
  };

  const bulkActions = [
    {
      label: t('Delete templates'),
      onClick: (ids: string[]) => handleBulkDeleteTemplates(ids),
      icon: <PiTrashLight className="h-4 w-4" />,
    },
  ];
 
  const getDraftTypeColor = (draftType: string): 'purple' | 'indigo' | 'blue' | 'cyan' => {
    switch (draftType) {
      case 'nda': return 'purple';
      case 'contract': return 'indigo';
      case 'memo': return 'blue';
      case 'pleading': return 'cyan';
      default: return 'blue';
    }
  };
 
  const getSections = (template: Template) => {
    const sections = Array.isArray(template.Structure)
      ? template.Structure  
      : template.Structure?.outline || [];
    return sections;
  };
 
  const getSharedWith = (template: Template) => {
    if (Array.isArray(template.SharedWith)) {
      return template.SharedWith;
    }
    if (typeof template.SharedWith === 'string') {
      try {
        return JSON.parse(template.SharedWith);
      } catch {
        return [];
      }
    }
    return [];
  };
 
  const tableData = useMemo(() => {
    return templates.map((template) => {
      const sections = getSections(template);
      const sharedWith = getSharedWith(template);
      const status = template.status || 'completed';
      const isProcessingActive = status === 'processing' || status === 'running';
      const isPending = status === 'pending';
      const isQueued = status === 'queued';
      const isFailed = status === 'failed';
      const isCompleted = status === 'completed' || status === 'ready';
      const isClickable = isCompleted;
 
      // Owner first, then SharedWith - RAW rows.
      //
      // This used to hand-map each row to `{ name, initials, avatarURL }` and
      // read the picture from exactly one key (`ProfilePicture`), so a payload
      // that spells it `profile_picture` or `avatar_url` lost it and every face
      // fell through to a grey silhouette. `CollaboratorStack` normalises
      // through `accessModel`, which tries every spelling - so nothing is
      // dropped on the way in. `Access: 'admin'` is the only thing added, since
      // "this row is the owner" is not in the payload.
      const allCollaborators: any[] = [
        ...(template.Owner ? [{ ...template.Owner, Access: 'admin' }] : []),
        ...sharedWith,
      ];
 
      const liveState = processingStates[template.id];

      return {
        id: template.id,
        processing_status: status,
        _isClickable: isClickable,
        'Name': (
          <div className="flex flex-col gap-0.5" style={{ opacity: isFailed ? 0.6 : 1 }}>
            {isProcessingActive ? (
              <div className="flex items-center gap-2">
                <MiniLoadingToCheckmark isLoading={true} />
                <div className="flex-1 min-w-0">
                  {liveState?.step ? (
                    <>
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-info-600 dark:text-info-400 font-medium truncate">
                          {(liveState.step || '').replace(/_/g, ' ')}
                        </span>
                      </div>
                      {liveState.message && (
                        <div className="text-xs text-fg-muted truncate mt-0.5">
                          {liveState.message}
                        </div>
                      )}
                      {liveState.percent != null && liveState.percent > 0 && (
                        <div className="mt-1 h-1 w-full bg-surface-sunken rounded-full overflow-hidden">
                          <div
                            className="h-full bg-champagne transition-all duration-700 ease-in-out rounded-full"
                            style={{ width: `${liveState.percent}%` }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-fg-muted">{t('Processing template...')}</div>
                      <div className="h-3 w-2/3 bg-surface-sunken rounded mt-1 animate-fadePulse" />
                    </>
                  )}
                </div>
              </div>
            ) : isFailed ? (
              <div className="flex items-center gap-2">
                <div className="text-sm text-danger-600 dark:text-danger-400">{t('Processing failed')}</div>
              </div>
            ) : (isPending || isQueued) ? (
              <div className="flex items-center gap-2">
                <PiHourglassLight className="h-4 w-4 text-warning-400 animate-pulse flex-shrink-0" />
                <div>
                  <div className="text-sm text-fg-muted">
                    {isPending ? t('Waiting to be queued...') : t('Queued for processing...')}
                  </div>
                </div>
              </div>
            ) : (
              <span className="text-fg-strong truncate">{template.TemplateName}</span>
            )}
          </div>
        ),
        'Document Type': !isCompleted ? (
          <span className="text-xs text-fg-muted">&mdash;</span>
        ) : (
          <Badge className='capitalize' color={getDraftTypeColor(template.DraftType)}>
            {/* CSS `capitalize` turns "nda" into "Nda"; legal acronyms must stay
                upper-case. Map the known ones, fall back to the translated label
                (capitalize handles normal words like "agreement"). */}
            {({ nda: 'NDA', spa: 'SPA', msa: 'MSA', dpa: 'DPA', sla: 'SLA', loi: 'LOI' }[(template.DraftType || '').toLowerCase()]) || t(template.DraftType)}
          </Badge>
        ),
        'Status': (
          <div className="flex items-center gap-2">
            <Badge
              className='capitalize'
              color={processingStatusToColorMap[status] || 'gray'}
              icon={
                isProcessingActive ? <BrandLoader size="xs" /> :
                (isPending || isQueued) ? <PiClockLight className="text-warning-400" /> :
                undefined
              }
            >
              {isProcessingActive ? t('Processing') :
               isQueued ? t('Queued') :
               isPending ? t('Pending') :
               isFailed ? t('Failed') :
               t('Completed')}
            </Badge>
          </div>
        ),
        'Sections': !isCompleted ? (
          <span className="text-xs text-fg-muted">&mdash;</span>
        ) : (
          <Badge color="gray">{sections.length}</Badge>
        ),
        /* `Owner` is the creator - it is also the first entry in the access
           stack beside it, which is correct: the person who made a template
           can always see it. The stamp rides under the name because "created
           by" is a claim about a person AND a moment; `CreatedAt` was on the
           payload and rendered nowhere. */
        'Created by': (
          <PersonCell
            person={template.Owner}
            meta={template.CreatedAt ? formatDateTime(template.CreatedAt) : undefined}
          />
        ),
        'Has access': allCollaborators.length > 0 ? (
          <CollaboratorStack collaborators={allCollaborators} max={4} />
        ) : (
          <span className="text-xs text-fg-muted">—</span>
        ),
        'Updated At': formatDateTime(template.UpdatedAt),
        rawTemplate: template,
      };
    });
  }, [templates, processingStates, t]);

  // ─── Filtered table-column templates (client-side search + owner filter) ──────────
  const filteredTableColTemplates = useMemo(() => {
    let result = tableColumnTemplates;
    // Apply owner filter
    const ownerFilter = selectedFilters['TableColOwner'] || [];
    if (ownerFilter.length > 0) {
      if (ownerFilter.includes('pons') && !ownerFilter.includes('mine')) {
        result = result.filter((tpl: any) => tpl.is_global);
      } else if (ownerFilter.includes('mine') && !ownerFilter.includes('pons')) {
        result = result.filter((tpl: any) => !tpl.is_global);
      }
    }
    // Apply search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((tpl: any) =>
        (tpl.name || '').toLowerCase().includes(q) ||
        (tpl.description || '').toLowerCase().includes(q) ||
        (tpl.category || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [tableColumnTemplates, searchQuery, selectedFilters]);

  // ─── Table Column Templates table data ─────────────────────────────
  const tableColData = useMemo(() => {
    return filteredTableColTemplates.map((tpl: any) => {
      const cols = tpl.columns || [];
      const isPons = tpl.is_global && !tpl.owner;

      // Raw rows - see the note on `allCollaborators` above.
      const tplCollaborators: any[] = isPons
        ? []
        : (Array.isArray(tpl.shared_with) ? tpl.shared_with : []);

      return {
        id: tpl.uuid || tpl.id,
        _isGlobal: !!tpl.is_global,
        'Name': <span>{tpl.name || t('Untitled')}</span>,
        'Category': (
          <Badge className='capitalize' color='blue'>
            {t((tpl.category || 'general').replace(/_/g, ' '))}
          </Badge>
        ),
        'Columns': <Badge color='gray'>{cols.length}</Badge>,
        /*
          `owner` is `TemplateOwner` - user_id, name, email, initials, and NO
          picture. So this cell is a name over an initials avatar, which is
          what `ProfileImage` falls back to anyway. Adding `profile_picture`
          to the owner payload is a backend follow-up; nothing here needs to
          change when it lands, since `PersonCell` normalises every spelling.

          A PONS template has no owner in the org, so it gets the mark rather
          than a dash - "PONS made this" is the honest answer to who created it.
        */
        'Created by': isPons ? (
          <Tooltip title={t('Provided by PONS.')}>
            <div className="flex items-center gap-2">
              <Logo variant="mark" whiteLabelAware={false} className="h-5 w-5 flex-shrink-0" />
            </div>
          </Tooltip>
        ) : (
          <PersonCell
            person={tpl.owner}
            meta={tpl.created_at ? formatDateTime(tpl.created_at) : undefined}
          />
        ),
        'Has access': isPons ? (
          <Tooltip title={t('This is a default template provided by PONS and is accessible to everyone in your organization.')}>
          <div className="flex items-center gap-2">
            <Logo variant="mark" whiteLabelAware={false} className="h-5 w-5 flex-shrink-0" />
          </div>
          </Tooltip>
        ) : tplCollaborators.length > 0 ? (
          <CollaboratorStack collaborators={tplCollaborators} max={4} />
        ) : (
          <span className="text-xs text-fg-muted">—</span>
        ),
        'Updated At': formatDateTime(tpl.updated_at || tpl.created_at),
      };
    });
  }, [filteredTableColTemplates, t]);

  // ─── Playbook filtered list ────────────────────────────────────
  const filteredPlaybooks = useMemo(() => {
    const ownerFilter = selectedFilters['PlaybookOwner'] || [];
    if (ownerFilter.length === 0) return playbooks;
    if (ownerFilter.includes('pons') && !ownerFilter.includes('mine')) return playbooks.filter((pb) => pb.is_global);
    if (ownerFilter.includes('mine') && !ownerFilter.includes('pons')) return playbooks.filter((pb) => !pb.is_global);
    return playbooks;
  }, [playbooks, selectedFilters]);

  // ─── Playbook table data ──────────────────────────────────────────
  const statusColorMap: Record<ReviewStatus, BadgeColor> = {
    draft: 'gray',
    active: 'green',
    archived: 'orange',
  };

  /* WHO MADE A PLAYBOOK is `accessModel.ownerOf` now - the picker modals had
     each grown their own answer to the same question, and disagreed. */

  const playbookData = useMemo(() => {
    return filteredPlaybooks.map((pb) => {
      const isExtracting = pb.extraction_status === 'processing';
      const liveState = playbookProcessingStates[pb.uuid];
      const isPonsPlaybook = pb.is_global && (pb.has_access || []).length === 0;
      // Raw rows - see the note on `allCollaborators` above.
      const pbCollaborators: any[] = pb.has_access || [];

      return {
        id: pb.uuid,
        _isClickable: !isExtracting,
        _isGlobal: !!pb.is_global,
        'Name': (
          <div className="flex items-center gap-2">
            {isExtracting && <BrandLoader size="xs" className="!h-4 !w-4 shrink-0" />}
            <span className={clsx('text-fg-strong truncate', isExtracting && 'opacity-70')}>
              {pb.name}
            </span>
            {isExtracting && liveState?.message && (
              <span className="text-xs text-info-500 truncate max-w-[200px]">{liveState.message}</span>
            )}
          </div>
        ),
        'Agreement Types': (
          <div className="flex flex-wrap gap-1">
            {(pb.agreement_types || []).slice(0, 2).map((at, i) => (
              <Badge key={i} color="blue" className="capitalize text-xs">{at}</Badge>
            ))}
            {(pb.agreement_types || []).length > 2 && (
              <Badge color="gray" className="text-xs">+{pb.agreement_types.length - 2}</Badge>
            )}
            {(pb.agreement_types || []).length === 0 && <span className="text-xs text-fg-muted">—</span>}
          </div>
        ),
        'Clause Rules': <Badge color="gray">{pb.clause_count}</Badge>,
        'Global Rules': <Badge color="gray">{pb.global_rule_count}</Badge>,
        /*
          NO CREATOR IN THE PAYLOAD. `Playbook` carries `has_access[]` and
          nothing else about who made it - no `owner`, no `created_by`. Adding
          one is a backend follow-up.

          Until then the best available answer is the admin-level entry in
          `has_access`, which is the creator wherever the backend sets the
          level. That is READ, not assumed: when no entry claims admin the
          cell is an em-dash rather than the first name in the list, because
          "someone has access" is not the same claim as "someone made this".
        */
        'Created by': isPonsPlaybook ? (
          <Tooltip title={t('Provided by PONS.')}>
            <div className="flex items-center gap-2">
              <Logo variant="mark" whiteLabelAware={false} className="h-5 w-5 flex-shrink-0" />
            </div>
          </Tooltip>
        ) : (
          /* The RAW row, not a normalised one. `PersonCell` normalises what it
             is given, and normalising an `AccessPerson` a second time used to
             drop the picture - so this cell showed initials while the access
             stack beside it, which normalises once, showed the photo. The
             owner is FOUND through the model and then passed through as it
             arrived. */
          <PersonCell
            person={ownerOf(pbCollaborators)}
            meta={pb.created_at ? formatDateTime(pb.created_at) : undefined}
          />
        ),
        'Has access': isPonsPlaybook ? (
          <Tooltip title={t('This is a default playbook provided by PONS and is accessible to everyone in your organization.')}>
            <div className="flex items-center gap-2">
              <Logo variant="mark" whiteLabelAware={false} className="h-5 w-5 flex-shrink-0" />
            </div>
          </Tooltip>
        ) : pbCollaborators.length > 0 ? (
          <CollaboratorStack collaborators={pbCollaborators} max={4} />
        ) : (
          <span className="text-xs text-fg-muted">—</span>
        ),
        'Updated At': pb.updated_at ? formatDateTime(pb.updated_at) : '—',
      };
    });
  }, [filteredPlaybooks, playbookProcessingStates, t]);

  if (isAddForm || templateId) {
    /* `h-full` alone is not a chain: these forms are flex columns whose
       bodies are `flex-1 min-h-0`, and a plain block parent gives them
       nothing to measure against - so the body sized to its content and
       scrolled inside a page that also scrolled. */
    const formHost = 'h-full min-h-0 flex flex-col';
    if (activeTab === 'playbooks') {
      return <div className={formHost}><CreatePlaybookForm /></div>
    } else if (activeTab === 'table-columns') {
      return <div className={formHost}><TableColumnsForm templateId={templateId} /></div>;
    } else if (activeTab === 'structure') {
      return <div className={formHost}><StructureForm templateId={templateId} /></div>;
    } else if (activeTab === 'branding') {
      return <div className={formHost}><TemplateForm templateId={templateId} type="branding" /></div>
    }
  }
 
  if (isInvalidType) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <EmptyState
          icon={<PiListLight />}
          headline={t('Invalid template type')}
          subtext={t('That template library does not exist. Head back to the document structure templates.')}
          button={{ label: t('Go to templates'), onClick: () => history.push('/templates?type=structure') }}
        />
      </div>
    );
  }
 
  return (
    <div className="flex flex-col h-full">
      <div data-tour-id="templates-list-header">
        <Header actions={actions} />
      </div>
      <div data-tour-id="templates-list-tabs">
      <FilterBarWithTabs
        itemCount={showTrash ? trashCount.showing : activeTab === 'playbooks' ? filteredPlaybooks.length : activeTab === 'table-columns' ? filteredTableColTemplates.length : templates.length}
        totalItemCount={showTrash ? trashCount.total : activeTab === 'playbooks' ? playbooksTotalItems : activeTab === 'table-columns' ? tableColumnTemplates.length : totalTemplates}
        itemCountLabel={showTrash ? t('items') : t(activeTab === 'playbooks' ? 'playbooks' : 'templates')}
        listLoading={isLoading && templates.length === 0 && filteredPlaybooks.length === 0 && filteredTableColTemplates.length === 0}
        tabs={templateTabs}
        activeTab={activeTab}
        onTabClick={handleTabClick}
        filters={activeTab === 'playbooks' ? [
          {
            id: 'PlaybookOwner',
            name: t('Owner'),
            options: [
              { value: 'mine', label: t('My playbooks') },
              { value: 'pons', label: t('PONS playbooks') },
            ],
          },
        ] : activeTab === 'table-columns' ? [
          {
            id: 'TableColOwner',
            name: t('Owner'),
            options: [
              { value: 'mine', label: t('My templates') },
              { value: 'pons', label: t('PONS templates') },
            ],
          },
        ] : []}
        selectedFilters={selectedFilters}
        onFilterChange={handleFilterChange}
        onClearAllFilters={handleClearAllFilters}
        sortOptions={[]}
        onSortChange={undefined}
        onSearchChange={handleSearchChange}
        sortValue={`${sortBy}_${sortOrder}`}
        actions={[
          ...bulkActions.map(action => ({
            label: action.label,
            onClick: () => action.onClick(selectedTemplates),
            icon: action.icon,
          })),
          ...(showTrash ? trashBulkActions : []),
        ]}
        selectionCount={(() => {
          if (showTrash) return trashSelectedCount;
          // Exclude system/global items from the actionable selection count
          // so the Actions dropdown only opens when the user has selected
          // editable items they own.
          let actionable = selectedTemplates;
          if (activeTab === 'playbooks') {
            const globals = new Set(playbooks.filter((pb: any) => pb.is_global).map((pb: any) => pb.uuid));
            actionable = selectedTemplates.filter((id) => !globals.has(id));
          } else if (activeTab === 'table-columns') {
            const globals = new Set(tableColumnTemplates.filter((t: any) => t.is_global).map((t: any) => t.uuid || t.id));
            actionable = selectedTemplates.filter((id) => !globals.has(id));
          }
          return actionable.length > 0 ? actionable.length : undefined;
        })()}
        recycleBinEnabled
        recycleBinActive={showTrash}
        recycleBinLabel={t('Templates')}
        onRecycleBinToggle={(active) => { if (!active) fetchInitialTemplates(); setShowTrash(active); }}
      />
      </div>

      {showTrash ? (
        <ResourceTrash
          onCountChange={(showing, total) => setTrashCount({ showing, total })}
          entityType={activeTab === 'playbooks' ? 'review' : activeTab === 'table-columns' ? 'table_template' : 'template'}
          entityLabel={activeTab === 'playbooks' ? 'playbooks' : activeTab === 'table-columns' ? 'table column templates' : 'templates'}
          emptyIcon={activeTab === 'playbooks' ? <PiBookOpenTextLight /> : activeTab === 'table-columns' ? <PiTableLight /> : <PiListLight />}
          onItemRestored={() => {
            fetchInitialTemplates();
          }}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
      // ONE scroller, and it is the table's.
      //
      // This was two boxes: an `overflow-hidden` column around an
      // `overflow-y-auto` one, around a Table that scrolls its own rows under a
      // sticky head. Three heights for one list, so the page scrolled the
      // table's whole box - sticky head and all - inside a scrollbar of its
      // own, next to the table's. The outer box measures the space and the
      // table fills it; only the rows move.
      //
      // `px-2` went with them. It was the reason the table looked inset from
      // the page while every other list runs to the edge.
      <div className="flex-1 min-h-0 flex flex-col">
        <div
          data-tour-id="templates-list-table"
          className="flex-1 min-h-0 flex flex-col pb-4 animate-fade-in"
        >
                {(activeTab === 'playbooks' ? isLoadingPlaybooks : activeTab === 'table-columns' ? isLoadingTableCols : isLoading) && (activeTab === 'playbooks' ? playbooks : activeTab === 'table-columns' ? filteredTableColTemplates : templates).length === 0 ? (
                  <Skeleton variant="table" rows={5} />
                ) : (
                  <Table
                    columns={columns}
                    data={activeTab === 'playbooks' ? playbookData : activeTab === 'table-columns' ? tableColData : tableData}
                    enableSelection
                    selectedIds={selectedTemplates}
                    onSelect={(id) => setSelectedTemplates(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                    onSelectAll={() => {
                      const allIds = activeTab === 'playbooks'
                        ? filteredPlaybooks.map(pb => pb.uuid)
                        : activeTab === 'table-columns'
                        ? filteredTableColTemplates.map((tpl: any) => tpl.uuid || tpl.id)
                        : templates.map(t => t.id);
                      setSelectedTemplates(selectedTemplates.length === allIds.length ? [] : allIds);
                    }}
                    actions={activeTab === 'playbooks' ? playbookActions : tableActions}
                    onRowClick={(row) => {
                      if (activeTab === 'playbooks') {
                        history.push(`/playbooks/${row.id}`);
                        return;
                      }
                      const status = row.processing_status;
                      if (status && status !== 'completed' && status !== 'ready') {
                        if (status === 'processing' || status === 'running') {
                          dispatch(showFeedbackModal({
                            showModal: true,
                            message: t('This template is still being processed. Please wait until processing completes.'),
                            modalType: 'info',
                            duration: 3000,
                          }));
                        } else if (status === 'failed') {
                          dispatch(showFeedbackModal({
                            showModal: true,
                            message: t('This template failed to process.'),
                            modalType: 'warning',
                            duration: 3000,
                          }));
                        }
                        return;
                      }
                      history.push(`/templates/${row.id}?type=${activeTab}`);
                    }}
                    hasMore={activeTab === 'table-columns' || activeTab === 'playbooks' ? false : hasMore}
                    isLoadingMore={activeTab === 'playbooks' ? isLoadingPlaybooks : activeTab === 'table-columns' ? isLoadingTableCols : isLoading}
                    onLoadMore={activeTab === 'table-columns' || activeTab === 'playbooks' ? undefined : loadMoreTemplates}
                    sortableColumns={sortableColumns}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSortChange}
                    total_items={activeTab === 'playbooks' ? playbooksTotalItems : activeTab === 'table-columns' ? tableColumnTemplates.length : totalTemplates}
                    total_showing_items={activeTab === 'playbooks' ? filteredPlaybooks.length : activeTab === 'table-columns' ? filteredTableColTemplates.length : templates.length}
                    setIsShowing={true}
                    showingLabel={t(activeTab === 'playbooks' ? 'playbooks' : 'templates')}
                    selectedCount={selectedTemplates.length}
                    resizableColumns={true}
                    defaultColumnWidths={activeTab === 'playbooks' ? {
                      'Name': 250,
                      'Agreement Types': 200,
                      'Clause Rules': 120,
                      'Global Rules': 120,
                      'Created by': 180,
                      'Has access': 160,
                      'Updated At': 180,
                    } : activeTab === 'table-columns' ? {
                      'Name': 250,
                      'Category': 150,
                      'Columns': 100,
                      'Created by': 180,
                      'Has access': 160,
                      'Updated At': 180,
                    } : {
                      'Name': 250,
                      'Document Type': 150,
                      'Status': 130,
                      'Sections': 100,
                      'Created by': 180,
                      'Has access': 160,
                      'Updated At': 180,
                    }}
                    storageKeyPrefix={activeTab === 'playbooks' ? 'templates-playbooks' : activeTab === 'table-columns' ? 'templates-table-cols' : 'templates'}
                    /* The box above is measured, so the table takes the height
                       it is given. Table's default cap is a viewport guess and
                       would only ever fall short of it. */
                    max="max-h-none"
                    min="min-h-0"
                    emptyState={{
                      icon: activeTab === 'playbooks' ? <PiBookOpenTextLight /> : activeTab === 'table-columns' ? <PiTableLight /> : activeTab === 'branding' ? <PiPaletteLight /> : <PiListLight />,
                      headline: activeTab === 'playbooks' ? t('No playbooks yet.') : activeTab === 'table-columns' ? t('No table column templates.') : activeTab === 'branding' ? t('No document branding templates available.') : t('No document structure templates.'),
                      subtext: activeTab === 'playbooks' ? t('Create playbooks to define contract review standards with clause rules and global rules.') : activeTab === 'table-columns' ? t('Create table column templates to define reusable column configurations for batch tables.') : activeTab === 'branding' ? t('Create branding templates to ensure consistent firm styling across all legal documents and communications.') : t('Develop templates or reviews to standardize processes, reduce drafting time, and maintain compliance.'),
                      button: {
                        label: activeTab === 'playbooks' ? t('Create new playbook') : activeTab === 'table-columns' ? t('Create new table columns template') : activeTab === 'branding' ? t('Create new branding template') : t('Create new structure template'),
                        onClick: () => history.push(`/templates/add?type=${activeTab}`),
                      }
                    }}
                  />
                )}
        </div>
      </div>
      )}
      {/* Audit Log Side Panel */}
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel
            entityType={auditTarget.type}
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </ActivityDrawer>
      )}
      <FeatureTour
        steps={templatesListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};
 
export default TemplatesList;